import firebase from "firebase";
import {deserialize, serialize} from "serializr";
import {AdUnitModel} from "@/modules/redactor/models/ad-settings/AdUnitModel";

export default {
    namespaced: true,
    name: "redactor-store-ad-unit",
    state: {
        mRootPath: null,
        mGlobalUID: null,
        mConfigUID: null,
        mReadonly: false,
        mValues: []
    },
    getters: {
        getRootPath: (state) => () => {
            return state.mRootPath;
        },
        getGlobalUID: (state) => () => {
            return state.mGlobalUID;
        },
        getConfigUID: (state) => () => {
            return state.mConfigUID;
        },
        getReadonly: (state) => () => {
            return state.mReadonly;
        },
        getConfigPath: (state) => (global, subpath) => {
            let result = null;

            if (global === true) {
                result = state.mRootPath + state.mGlobalUID;
            } else if (global === false) {
                result = state.mRootPath + state.mConfigUID;
            } else {
                console.assert(false);
            }

            if (result && subpath) {
                result = result + subpath;
            }

            return result;
        },

        getValue: (state) => (uid) => {
            return state.mValues.find(v => v.uid === uid) || null;
        },
        getValues: (state) => () => {
            return state.mValues;
        }
    },
    mutations: {
        setRootPath(state, value) {
            state.mRootPath = value;
        },
        setGlobalUID(state, value) {
            state.mGlobalUID = value;
        },
        setConfigUID(state, value) {
            state.mConfigUID = value;
        },
        setReadonly(state, readonly) {
            state.mReadonly = readonly;
        },

        setValues(state, values) {
            state.mValues = values;
        },
        addValues(state, values) {
            let uids = values.map(v => v.uid);

            state.mValues = state.mValues.filter(v => {
                return uids.indexOf(v.uid) < 0;
            }).concat(values);
        },
        updateValues(state, values) {
            values.forEach(value => {
                let index = state.mValues.findIndex(v => {
                    return v.uid === value.uid;
                });

                if (index >= 0) {
                    state.mValues.splice(index, 1, value);
                } else {
                    state.mValues.push(value);
                }
            });
        },
        updateValue(state, value) {
            value.changed = true;

            state.mValues.forEach(v => {
                v.changed = false;
            });

            let index = state.mValues.findIndex(v => {
                return v.uid === value.uid;
            });

            if (index >= 0) {
                state.mValues.splice(index, 1, value);
            } else {
                state.mValues.push(value);
            }
        },
        removeValue(state, value) {
            let index = state.mValues.findIndex(v => {
                return v.uid === value.uid;
            });

            if (index >= 0) {
                state.mValues.splice(index, 1);
            }
        },
      setValuesForScope(state, { values, global, shared }) {
        const isShared = shared || false;
        const uids = new Set(values.map(v => v.uid));

        state.mValues = state.mValues
          .filter(v => !(v.global === global && Boolean(v.shared) === isShared))
          .filter(v => !uids.has(v.uid))
          .concat(values);
      }
    },
    actions: {
        init({commit}, payload) {
            console.info('init store-ad-unit')
            commit("setRootPath", payload.root_path);
            commit("setGlobalUID", payload.global_uid);
            commit("setConfigUID", payload.config_uid);
            commit("setReadonly", payload.readonly);
        },

        load({commit}, payload) {
            console.info('load store-ad-unit');

          if (!payload.config) {
            commit("setValuesForScope", { values: [], global: payload.global, shared: payload.shared || false });
            return;
          }

          const values = Object.values(payload.config);
          const items = values.map(v => deserialize(AdUnitModel, v));

          items.forEach(v => { v.global = payload.global; v.shared = payload.shared || false; });

          commit("setValuesForScope", { values: items, global: payload.global, shared: payload.shared || false });
        },

        flush({dispatch}) {
            dispatch('pushValues');
        },

        clear({commit}) {
            commit("setValues", [])
        },

        validate({getters}) {
            let errors = [];

            getters.getValues().filter(v => v.enabled).forEach(v => {
                try {
                    if (!v.valid()) {
                        errors.push({name: "ad-unit", key: v.uid});
                    }
                } catch (error) {
                    errors.push({name: "ad-unit", key: v.uid, error: error.message});
                }
            });

            if (errors.length > 0) {
                return Promise.reject(errors);
            } else {
                return Promise.resolve();
            }
        },

        buildPatch({dispatch, getters}) {
            return dispatch("validate").then(() => {
                let result = {};
                let values = getters.getValues().filter(v => v.export);

                if (values.length > 0) {
                    result["orders"] = values.reduce((result, v) => {
                        result[v.uid] = serialize(AdUnitModel, v);
                        return result;
                    }, {});
                }

                return Promise.resolve(result);
            });
        },

        resetPatch({dispatch, getters}) {
            let values = getters.getValues().filter(v => v.export).map(v => {
                let value = v.clone();
                value.export = false;
                value.changed = false;
                return value;
            });

            if (values.length > 0) {
                return dispatch('pushValues', values);
            } else {
                return Promise.resolve();
            }
        },

        pushValue({commit, getters}, value) {
            if (getters.getReadonly()) {
                return Promise.reject("The config is read-only");
            } else if (value.global) {
                return Promise.reject("It is forbidden to modify a global record");
            }

            let data = serialize(AdUnitModel, value);

            return firebase.database()
                .ref(getters.getConfigPath(false, "/ad-units/" + value.uid))
                .set(data, error => {
                    if (error) {
                        console.error("push failed: " + error);
                    } else {
                        commit("updateValue", value);
                    }
                });
        },

        pushValues({commit, getters}, values) {
            if (getters.getReadonly()) {
                return Promise.reject("The config is read-only");
            }

            let update = true;

            if (!values) {
                values = getters.getValues();
                update = false;
            }

            let data = values.filter(v => !v.global && !v.shared).reduce((result, v) => {
                result[v.uid] = serialize(AdUnitModel, v);
                return result;
            }, {});

            let ref = firebase.database().ref(getters.getConfigPath(false, "/ad-units"));

            if (update) {
                return ref.update(data, error => {
                    if (error) {
                        console.error("publish failed: " + error);
                    } else if (update) {
                        commit("updateValues", values);
                    }
                });
            } else {
                return ref.set(data, error => {
                    if (error) {
                        console.error("publish failed: " + error);
                    }
                });
            }
        },

        removeValue({commit, getters}, value) {
            if (getters.getReadonly()) {
                return Promise.reject("The config is read-only");
            } else if (value.global) {
                return Promise.reject("It is forbidden to delete a global record");
            }

            return firebase.database()
                .ref(getters.getConfigPath(false, "/ad-units/" + value.uid))
                .set(null, error => {
                    if (error) {
                        console.error("remove failed: " + error);
                    } else {
                        commit("removeValue", value);
                    }
                });
        },

        removeBulkValues({commit, getters, rootGetters}, values) {
            if (getters.getReadonly()) {
                return Promise.reject("The config is read-only");
            }

            const globalValues = values.filter(v => v.global);
            if (globalValues.length > 0) {
                return Promise.reject("It is forbidden to delete global records");
            }

            const configUID = rootGetters["redactor-store/getConfigUID"]();
            const entityType = "ad-units";
            const uids = values.map(v => v.uid);

            return Promise.all([
                firebase.auth().currentUser.getIdToken()
                    .then((token) => {
                        let requestOptions = {
                            method: "DELETE",
                            headers: { "Content-Type": "application/json", 'Authorization': token},
                            body: JSON.stringify({ uids })
                        };
                        return fetch(process.env.VUE_APP_FB_BACKEND_URL + `deleteConfigs/${configUID}/${entityType}`, requestOptions)
                            .then(async data => {
                                if (data.status != 200) {
                                    return data.text()
                                        .then((data)=>{
                                            throw data
                                        })
                                        .catch((error) => {
                                            throw error
                                        })
                                }else{
                                    // Remove all values from store after successful API call
                                    values.forEach(value => {
                                        commit("removeValue", value);
                                    });
                                }
                            })
                            .catch((error) => {
                                throw error
                            })
                    })
            ])
        },
    }
}